import { StyleSheet, View } from "react-native";
import { ModDashboardQueue } from "./moddashboardqueue";
import { ModerationFilters } from "./moddashboardFilters";
import { ModDashboardUserStatistics, useUserProfileModerationData } from "./moddashboardProfileUtils";
import { useEffect, useState } from "react";
import { ThinTag } from 'open-modules/zdf/component/button';
import { BlockConfirmationModal, UnblockConfirmationModal } from "./judgementcard";
import { useDatastore, DEPRECATED_useModulePublicData, usePersonaKey, usePersonaObject } from "util/datastore";
import { colorBorderPrimary, colorSurfacePrimary, colorSurfaceSecondary, colorSurfaceWarning, colorTextSecondary } from 'component/color';
import { HorizBox, Pad, PadBox } from 'component/basics';
import { LoadingScreen } from "component/banner";
import { Heading, UtilityText } from 'component/text';
import { LetterFace, ProfilePhoto } from 'component/people';
import { CTAButton } from 'component/button';
import { getQueueSubsetCounts, useFilteredQueuesOfUser } from "./moddashboardqueuehooks";
import { setToastMessage } from 'open-modules/zdf/component/toast';
import { setBlockStatusAsync, SuspensionStatusType, useUserSuspensions } from "open-modules/zdf/component/suspension/suspension";
import { t } from "util/stdtypes";

export function ModDashboardUserProfile({userId}) {
    return <View style={ModDashboardUserProfileStyle.page}>
        <Heading label={t("User Profile")} level={1} type="large" weight="medium"/>
        <ModDashboardUserProfileInformation userId={userId}/>
        <View>
            <Pad size={24} />
            <Heading label={t("All comments")} />
            <ModDashboardQueue
                structureKey={"profile"}
                instanceKey={userId}
                useAllFilteredQueues={useFilteredQueuesOfUser}
                useQueueCounts={getQueueSubsetCounts}
                additionalFilters={[ModerationFilters.Warned, ModerationFilters.Deleted]}
            />
        </View>
    </View>
}

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function ModDashboardUserProfileInformation({ userId }) {
    const datastore = useDatastore();
    const [counter, setCounter] = useState(0)

    const userProfileModerationData = useUserProfileModerationData({ userId })
    const userSuspensions = useUserSuspensions({userKey: userId, counter: counter})
    const warningCount = userSuspensions.activeWarnings.length;
    const isBlocked = userSuspensions.latestBlock?.suspensionType === SuspensionStatusType.BLOCKED
    const blockedByName = userSuspensions.latestBlock?.blockedByName;


    const [showBlockConfirmationModal, setShowBlockConfirmationModal] = useState(false);
    const [showUnblockConfirmationModal, setShowUnblockConfirmationModal] = useState(false);

    async function onBlock() {
        try {
            await setBlockStatusAsync({datastore, userKey: userId, value: true, shouldSendEmail: true})
            setShowBlockConfirmationModal(false)
            setToastMessage({datastore: datastore, label: t('{user} was successfully blocked'), visible: true, formatParams: {user: preview.name}})
            setCounter(counter+1)
        } catch (e) {
            setShowBlockConfirmationModal(false)
            setToastMessage({datastore: datastore, label: t("Operation could not be executed, please try again"), visible: true, type:"error" })
            setCounter(counter+1)
        }
    }

    async function onUnblock() {
        try {
            await setBlockStatusAsync({ datastore, userKey: userId, value: false, shouldSendEmail: true })
            setShowUnblockConfirmationModal(false)
            setToastMessage({datastore: datastore, label: t('{user} was successfully unblocked'), visible: true, formatParams: {user: preview.name}})
            setCounter(counter+1)
            
        } catch (error) {
            setShowUnblockConfirmationModal(false)
            setToastMessage({datastore: datastore, label: t("Operation could not be executed, please try again"), visible: true, type: "error" })
            setCounter(counter+1)
        }
    }

    if (!userProfileModerationData || !userProfileModerationData.preview || !userSuspensions ) {
        return <LoadingScreen />;
    }
    const preview = userProfileModerationData?.preview;

    return (
        <View style={ModDashboardUserProfileStyle.informationBox}>
            <HorizBox center>
                {preview.photoUrl ? (
                    <ProfilePhoto type="huge" photo={preview.photoUrl} />
                ) : (
                    <LetterFace type="huge" name={preview.name} hue={preview.hue} />
                )}
                <Pad size={16} />
                <View style={{ gap: 2 }}>
                    <HorizBox center>
                        <Heading text={preview.name} />
                        {isBlocked && (
                            <>
                                <Pad size={8} />
                                <ThinTag emoji={"🚫"} label={t("Blocked")} backgroundColor={colorSurfaceWarning} />
                            </>
                        )}
                    </HorizBox>
                    <HorizBox>
                        <UtilityText color={colorTextSecondary} label={t("Member since ")} />
                        <UtilityText
                            color={colorTextSecondary}
                            text={new Date(preview.memberSince).getFullYear().toString()}
                        />
                    </HorizBox>
                </View>
            </HorizBox>
            <PadBox horiz={24}>
                <ModDashboardUserStatistics userProfileData={userProfileModerationData} />
            </PadBox>
            <View style={ModDashboardUserProfileStyle.settingsBox}>
                <Heading label={t("User Settings")} />
                <View>
                    <View style={{ width: 234 }}>
                        {warningCount < 2 && !isBlocked && <CTAButton label={t("Block user")} wide disabled />}
                        {warningCount >= 2 && !isBlocked && (
                            <>
                                <CTAButton
                                    wide
                                    type="delete"
                                    label={t("Block user")}
                                    onPress={() => setShowBlockConfirmationModal(true)}
                                />
                                {showBlockConfirmationModal && (
                                    <BlockConfirmationModal
                                        onBlock={onBlock}
                                        onClose={() => setShowBlockConfirmationModal(false)}
                                    />
                                )}
                            </>
                        )}
                        {isBlocked && (
                            <>
                                <CTAButton
                                    label={t("Unblock user")}
                                    wide
                                    type="delete"
                                    onPress={() => setShowUnblockConfirmationModal(true)}
                                />
                                {showUnblockConfirmationModal && (
                                    <UnblockConfirmationModal
                                        onUnblock={onUnblock}
                                        onClose={() => setShowUnblockConfirmationModal(false)}
                                    />
                                )}
                            </>
                        )}
                    </View>
                    <Pad size={16} />
                    <UtilityText
                        type="small"
                        label={
                            isBlocked
                                ? "User has already been warned {count} {noun} and was blocked by {blockedByName}."
                                : warningCount < 2
                                    ? "A user must be warned 2 times before they can be blocked."
                                    : "User has already been warned {count} {noun}."
                        }
                        formatParams={{ count: warningCount, blockedByName, singular: "time", plural: "times" }}
                    />
                </View>
            </View>
        </View>
    );
}

const ModDashboardUserProfileStyle = StyleSheet.create({
    page: { 
        gap: 24, 
        paddingTop: 80, 
        backgroundColor: colorSurfaceSecondary, 
        flexGrow: 1 },
    informationBox: {
        backgroundColor: colorSurfacePrimary,
        paddingVertical: 24,
        paddingHorizontal: 20,
        borderRadius: 4,
        gap: 24,
        overflow: "hidden"
    },
    settingsBox: {
        borderTopWidth: 1,
        borderColor: colorBorderPrimary,
        paddingTop: 16,
        gap: 16
    }
});


export { useUserProfileModerationData, ModDashboardUserStatistics } from "./moddashboardProfileUtils";
